import '@phosphor-icons/web/regular';
import 'leaflet/dist/leaflet.css';
import './styles.css';
import { dictionaries, fieldLabels, type Dictionary } from './i18n';
import { flattenIdentity, generateIdentity } from './generator';
import { identityCatalogStats } from './identityCatalog';
import { localizedStateName, territoryCodes } from './states';
import { platformTaxSources, taxStates } from './tax';
import type { GeneratedIdentity, Locale, LocationRecord } from './types';

const BRAND_NAME = 'US Address Lab';
const appElement = document.querySelector<HTMLDivElement>('#app');
if (!appElement) throw new Error('App root not found');
const app = appElement;

type Page = 'home' | 'guide' | 'tax-guide' | 'about' | 'faq';
type StoredFilters = { state: string; city: string; requireHouseNumber: boolean; taxFreeOnly: boolean };
const taxFreeStateCodes = new Set<string>(taxStates.map((state) => state.code));

const getLocale = (): Locale => {
  const routeLocale = location.pathname.split('/').filter(Boolean)[0];
  if (routeLocale === 'zh' || routeLocale === 'en') return routeLocale;
  const saved = localStorage.getItem('locale');
  return saved === 'zh' || saved === 'en' ? saved : navigator.language.toLowerCase().startsWith('zh') ? 'zh' : 'en';
};

const getPage = (): Page => {
  const part = location.pathname.split('/').filter(Boolean)[1];
  return part === 'guide' || part === 'tax-guide' || part === 'about' || part === 'faq' ? part : 'home';
};

let locale = getLocale();
let page = getPage();
let locations: LocationRecord[] = [];
let currentIdentity: GeneratedIdentity | null = null;
let filters: StoredFilters = loadFilters();
let mapCleanup: (() => void) | null = null;

if (!/^\/(zh|en)(\/|$)/.test(location.pathname)) {
  history.replaceState({}, '', `/${locale}/`);
}

function loadFilters(): StoredFilters {
  try {
    const value = JSON.parse(localStorage.getItem('addressLabFilters') || 'null') as Partial<StoredFilters> | null;
    return { state: '', city: '', requireHouseNumber: value?.requireHouseNumber ?? true, taxFreeOnly: false };
  } catch {
    return { state: '', city: '', requireHouseNumber: true, taxFreeOnly: false };
  }
}

function saveFilters() {
  localStorage.setItem('addressLabFilters', JSON.stringify(filters));
}

const escapeHtml = (value: unknown) => String(value)
  .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;').replaceAll("'", '&#039;');

const route = (targetPage: Page, targetLocale = locale) => `/${targetLocale}/${targetPage === 'home' ? '' : `${targetPage}/`}`;

function shell(content: string, dictionary: Dictionary) {
  const otherLocale: Locale = locale === 'zh' ? 'en' : 'zh';
  return `
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="${route('home')}" data-nav="home" aria-label="${BRAND_NAME}">
          <span class="brand-mark" aria-hidden="true"><i class="ph ph-map-pin-area"></i></span>
          <span>${BRAND_NAME}</span>
        </a>
        <nav class="desktop-nav" aria-label="Primary navigation">
          ${navLink('home', dictionary.home)}${navLink('guide', dictionary.guide)}${navLink('tax-guide', dictionary.taxGuide)}${navLink('about', dictionary.about)}${navLink('faq', dictionary.faq)}
        </nav>
        <a class="language-switch" href="${route(page, otherLocale)}" data-language="${otherLocale}">
          <i class="ph ph-translate" aria-hidden="true"></i><span>${dictionaries[otherLocale].languageName}</span>
        </a>
      </div>
    </header>
    ${content}
    <footer class="site-footer">
      <div class="footer-inner">
        <div><strong>${BRAND_NAME}</strong><p>${dictionary.footerNote}</p></div>
        <div class="footer-links">
          <a href="https://www.geonames.org/" target="_blank" rel="noreferrer">${dictionary.geoCredit}</a>
          <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">${dictionary.osmCredit}</a>
        </div>
      </div>
    </footer>`;
}

function navLink(targetPage: Page, label: string) {
  return `<a href="${route(targetPage)}" data-nav="${targetPage}" ${page === targetPage ? 'aria-current="page"' : ''}>${label}</a>`;
}

function renderLoading() {
  const d = dictionaries[locale];
  app.innerHTML = shell(`<main id="main-content" class="content-shell"><div class="loading-panel" role="status"><span class="skeleton wide"></span><span class="skeleton"></span><span class="skeleton short"></span><p>${d.loading}</p></div></main>`, d);
  bindNavigation();
}

async function loadData() {
  renderLoading();
  try {
    const response = await fetch('/data/locations.json');
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    locations = await response.json() as LocationRecord[];
    sanitizeFilters();
    render();
  } catch (error) {
    console.error(error);
    const d = dictionaries[locale];
    app.innerHTML = shell(`<main id="main-content" class="content-shell"><div class="error-panel" role="alert"><i class="ph ph-warning-circle"></i><h1>${d.loadError}</h1><button class="button primary" id="retryButton">${d.generate}</button></div></main>`, d);
    bindNavigation();
    document.querySelector('#retryButton')?.addEventListener('click', loadData);
  }
}

function sanitizeFilters() {
  const states = new Set(locations.map((item) => item.s));
  if (filters.state && !states.has(filters.state)) filters.state = '';
  if (filters.taxFreeOnly && filters.state && !taxFreeStateCodes.has(filters.state)) filters.state = '';
  const validCity = locations.some((item) => (!filters.state || item.s === filters.state) && item.c === filters.city);
  if (!validCity) filters.city = '';
}

function scopedLocations() {
  return filters.taxFreeOnly ? locations.filter((item) => taxFreeStateCodes.has(item.s)) : locations;
}

function filteredLocations() {
  const available = scopedLocations();
  let pool = filters.state ? available.filter((item) => item.s === filters.state) : available;
  if (filters.city) {
    const cityPool = pool.filter((item) => item.c.toLocaleLowerCase() === filters.city.toLocaleLowerCase());
    if (cityPool.length) pool = cityPool;
  }
  return pool;
}

function createIdentity() {
  const pool = filteredLocations();
  const locationRecord = pool[Math.floor(Math.random() * pool.length)] || locations[0];
  if (!locationRecord) return;
  currentIdentity = generateIdentity(locationRecord, locale, filters.requireHouseNumber);
}

function render(options: { scrollToTop?: boolean; restoreFocus?: string } = {}) {
  const { scrollToTop = true, restoreFocus } = options;
  const previousScrollY = window.scrollY;
  mapCleanup?.();
  mapCleanup = null;
  const d = dictionaries[locale];
  document.documentElement.lang = locale === 'zh' ? 'zh-CN' : 'en';
  const pageTitle = page === 'home' ? d.title : page === 'guide' ? d.guideTitle : page === 'tax-guide' ? d.taxTitle : page === 'about' ? d.aboutTitle : d.faqTitle;
  document.title = `${pageTitle} | ${BRAND_NAME}`;

  if (page === 'home') {
    if (!currentIdentity || currentIdentity.locale !== locale) createIdentity();
    app.innerHTML = shell(renderHome(d), d);
    bindHome();
  } else {
    app.innerHTML = shell(renderInfoPage(d), d);
  }
  bindNavigation();
  window.requestAnimationFrame(() => {
    window.scrollTo({ top: scrollToTop ? 0 : previousScrollY, behavior: 'instant' });
    if (restoreFocus) document.querySelector<HTMLElement>(restoreFocus)?.focus({ preventScroll: true });
  });
}

function renderHome(d: Dictionary) {
  if (!currentIdentity) return '';
  const availableLocations = scopedLocations();
  const stateMap = new Map<string, string>();
  availableLocations.forEach((item) => stateMap.set(item.s, item.n));
  const states = [...stateMap.entries()].sort((a, b) => locale === 'zh' ? a[0].localeCompare(b[0]) : a[1].localeCompare(b[1]));
  const regularStates = states.filter(([code]) => !territoryCodes.has(code));
  const territories = states.filter(([code]) => territoryCodes.has(code));
  const regions = stateMap.size;
  const stateOption = ([code, name]: [string, string]) => `<option value="${code}" ${filters.state === code ? 'selected' : ''}>${escapeHtml(locale === 'zh' ? localizedStateName(code, name) : name)} · ${code}</option>`;
  const addressForDisplay = Object.fromEntries(Object.entries(currentIdentity.address).filter(([key]) => !['latitude', 'longitude'].includes(key)));

  return `<main id="main-content" class="content-shell home-layout">
    <h1 class="visually-hidden">${d.title}</h1>
    <aside class="control-rail" aria-labelledby="filter-heading">
      <section class="quick-actions" aria-labelledby="quick-actions-heading">
        <div class="quick-actions-heading"><div><h2 id="quick-actions-heading">${d.quickActions}</h2><span>${escapeHtml(currentIdentity.address.city)}, ${currentIdentity.address.state}</span></div><time datetime="${currentIdentity.generatedAt}">${new Date(currentIdentity.generatedAt).toLocaleTimeString(locale === 'zh' ? 'zh-CN' : 'en-US', { hour: '2-digit', minute: '2-digit' })}</time></div>
        <div class="action-buttons">
          <button class="button secondary" id="copyAllButton"><i class="ph ph-copy" aria-hidden="true"></i>${d.copyAll}</button>
          <button class="button secondary" id="exportButton"><i class="ph ph-download-simple" aria-hidden="true"></i>${d.exportJson}</button>
        </div>
      </section>
      <div class="control-panel">
        <h2 id="filter-heading">${d.filterTitle}</h2>
        <label for="stateSelect">${d.state}</label>
        <select id="stateSelect">
          <option value="">${d.randomState}</option>
          <optgroup label="${d.statesGroup}">${regularStates.map(stateOption).join('')}</optgroup>
          <optgroup label="${d.territoriesGroup}">${territories.map(stateOption).join('')}</optgroup>
        </select>
        <label for="cityInput">${d.city}</label>
        <div class="city-combobox" id="cityCombobox">
          <div class="input-wrap"><i class="ph ph-magnifying-glass" aria-hidden="true"></i><input id="cityInput" role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="cityOptions" value="${escapeHtml(filters.city)}" placeholder="${d.cityPlaceholder}" autocomplete="off"></div>
          <div class="city-options" id="cityOptions" role="listbox" hidden></div>
        </div>
        <label class="check-row" for="taxFreeOnly"><input type="checkbox" id="taxFreeOnly" ${filters.taxFreeOnly ? 'checked' : ''}><span><strong>${d.taxFreeOnly}</strong><small>${d.taxFreeHelp}</small></span></label>
        <label class="check-row" for="houseNumber"><input type="checkbox" id="houseNumber" ${filters.requireHouseNumber ? 'checked' : ''}><span><strong>${d.houseNumber}</strong><small>${d.houseHelp}</small></span></label>
        <button class="button primary full" id="generateButton"><i class="ph ph-arrows-clockwise" aria-hidden="true"></i>${d.generate}</button>
      </div>
      <section class="popular-section" aria-labelledby="popular-heading">
        <h2 id="popular-heading">${d.popular}</h2>
        <div class="city-chips">${popularCities().map(({ c, s }) => `<button data-city="${escapeHtml(c)}" data-state="${s}">${escapeHtml(c)}</button>`).join('')}</div>
      </section>
      <section class="coverage" aria-labelledby="stats-heading"><h2 id="stats-heading">${d.stats}</h2><div><strong>${availableLocations.length.toLocaleString()}</strong><span>${d.records}</span></div><div><strong>${regions}</strong><span>${d.regions}</span></div></section>
    </aside>
    <div class="result-column">
      <div class="identity-stack" aria-live="polite">
        ${renderDataSection(d.basic, currentIdentity.person, false, 'ph-user-circle')}
        ${renderDataSection(d.address, addressForDisplay, false, 'ph-map-pin')}        
        ${renderDataSection(d.employment, currentIdentity.employment, false, 'ph-briefcase')}
        ${renderDataSection(d.testCredentials, Object.fromEntries(Object.entries(currentIdentity.paymentTestData).filter(([key]) => key !== 'simulatedOnly')), true, 'ph-credit-card', false, d.cardNotice)}
        ${renderDataSection(d.additional, currentIdentity.additional, false, 'ph-info', true)}
      </div>
      ${renderMapPanel(d)}
    </div>
    <div id="toast" class="toast" role="status" aria-live="polite"></div>
  </main>`;
}

function renderDataSection(title: string, data: object, sensitive = false, icon = 'ph-info', wide = false, notice = '') {
  return `<section class="data-card ${sensitive ? 'test-section' : ''} ${wide ? 'wide-card' : ''}"><header class="data-card-header"><span class="data-card-icon" aria-hidden="true"><i class="ph ${icon}"></i></span><h2>${title}</h2></header><dl>${Object.entries(data).map(([key, value]) => `
    <div class="data-row"><dt>${fieldLabels[locale][key] || key}</dt><dd>${escapeHtml(value)}</dd><button class="icon-button copy-field" data-copy="${escapeHtml(value)}" aria-label="${locale === 'zh' ? '复制' : 'Copy'} ${fieldLabels[locale][key] || key}" title="${locale === 'zh' ? '复制' : 'Copy'}"><i class="ph ph-copy" aria-hidden="true"></i></button></div>`).join('')}</dl>${notice ? `<p class="card-notice"><i class="ph ph-warning-circle" aria-hidden="true"></i><strong>${escapeHtml(notice)}</strong></p>` : ''}</section>`;
}

function renderMapPanel(d: Dictionary) {
  if (!currentIdentity) return '';
  const query = encodeURIComponent(`${currentIdentity.address.city}, ${currentIdentity.address.state} ${currentIdentity.address.zipCode}`);
  return `<section class="map-panel" aria-labelledby="map-heading"><div class="map-heading"><div><h2 id="map-heading">${d.mapTitle}</h2><p>${d.mapNote}</p></div><a class="button secondary" href="https://www.google.com/maps/search/?api=1&query=${query}" target="_blank" rel="noreferrer"><i class="ph ph-arrow-square-out" aria-hidden="true"></i>${d.openMaps}</a></div><div id="map" class="map-canvas" data-lat="${currentIdentity.address.latitude}" data-lng="${currentIdentity.address.longitude}" data-label="${escapeHtml(currentIdentity.address.city)}"><div class="map-placeholder"><i class="ph ph-map-trifold" aria-hidden="true"></i><span>${d.mapTitle}</span></div></div></section>`;
}

function renderInfoPage(d: Dictionary) {
  if (page === 'tax-guide') return renderTaxGuide(d);
  if (page === 'about') return renderAboutPage(d);
  if (page === 'faq') {
    return `<main id="main-content" class="content-shell prose-page"><header><h1>${d.faqTitle}</h1></header><div class="faq-list">${d.faqs.map(([question, answer]) => `<details><summary>${question}<i class="ph ph-plus" aria-hidden="true"></i></summary><p>${answer}</p></details>`).join('')}</div></main>`;
  }
  return `<main id="main-content" class="content-shell prose-page"><header><h1>${d.guideTitle}</h1><p>${d.guideIntro}</p></header><div class="info-grid">${d.guideSections.map(([heading, body], index) => `<article><span class="info-icon"><i class="ph ${['ph-map-pin', 'ph-arrows-clockwise', 'ph-brackets-curly', 'ph-map-trifold'][index]}" aria-hidden="true"></i></span><h2>${heading}</h2><p>${body}</p></article>`).join('')}</div></main>`;
}

function renderAboutPage(d: Dictionary) {
  const metrics = [identityCatalogStats.names, identityCatalogStats.streets, identityCatalogStats.companies, locations.length];
  const localePath = locale === 'zh' ? '/zh/' : '/en/';
  return `<main id="main-content" class="content-shell about-page">
    <header class="about-hero">
      <div class="about-hero-copy"><span class="eyebrow"><i class="ph ph-info" aria-hidden="true"></i>${d.aboutEyebrow}</span><h1>${d.aboutTitle}</h1><p>${d.aboutBody}</p><a class="button primary about-cta" href="${localePath}" data-nav="home"><i class="ph ph-arrow-right" aria-hidden="true"></i>${d.aboutCta}</a></div>
      <aside class="browser-card"><span class="browser-card-icon"><i class="ph ph-browser" aria-hidden="true"></i></span><h2>${d.aboutBrowserTitle}</h2><p>${d.aboutBrowserBody}</p><div class="trust-tags">${d.aboutBrowserTags.map((tag) => `<span><i class="ph ph-check" aria-hidden="true"></i>${tag}</span>`).join('')}</div></aside>
    </header>
    <section class="about-metrics" aria-label="${d.stats}">${metrics.map((value, index) => `<div><strong>${value.toLocaleString(locale === 'zh' ? 'zh-CN' : 'en-US')}+</strong><span>${d.aboutMetricLabels[index]}</span></div>`).join('')}</section>
    <section class="about-explainer">
      <article><span class="section-number" aria-hidden="true">01</span><h2>${d.aboutWhatTitle}</h2><p>${d.aboutWhatBody}</p></article>
      <article class="feature-list"><span class="section-number" aria-hidden="true">02</span><h2>${d.aboutFeaturesTitle}</h2><ul>${d.aboutFeatures.map((item) => `<li><i class="ph ph-check-circle" aria-hidden="true"></i><span>${item}</span></li>`).join('')}</ul></article>
    </section>
    <section class="privacy-panel"><div><span class="privacy-icon"><i class="ph ph-shield-check" aria-hidden="true"></i></span><h2>${d.aboutPrivacyTitle}</h2><p>${d.aboutPrivacyBody}</p></div><ul>${d.aboutPrivacyPoints.map((item) => `<li><i class="ph ph-lock-key" aria-hidden="true"></i><span>${item}</span></li>`).join('')}</ul></section>
    <section class="use-case-section"><div class="section-heading"><div><h2>${d.aboutUsesTitle}</h2><p>${d.aboutUsesIntro}</p></div><span class="state-count">03</span></div><div class="use-case-grid">${d.aboutUses.map(([title, body], index) => `<article><span class="use-case-icon"><i class="ph ${['ph-code', 'ph-checks', 'ph-presentation-chart'][index]}" aria-hidden="true"></i></span><h3>${title}</h3><p>${body}</p></article>`).join('')}</div></section>
    <aside class="about-notice"><i class="ph ph-warning-circle" aria-hidden="true"></i><div><h2>${d.aboutNoticeTitle}</h2><p>${d.aboutNoticeBody}</p></div></aside>
  </main>`;
}

function renderTaxGuide(d: Dictionary) {
  const sourceLabel = locale === 'zh' ? '查看州政府说明' : 'View state source';
  return `<main id="main-content" class="content-shell prose-page tax-page">
    <header>
      <span class="eyebrow"><i class="ph ph-receipt" aria-hidden="true"></i>${d.taxGuide}</span>
      <h1>${d.taxTitle}</h1>
      <p>${d.taxIntro}</p>
      <small>${d.taxUpdated}</small>
    </header>
    <section class="tax-section" aria-labelledby="tax-states-heading">
      <div class="section-heading"><div><h2 id="tax-states-heading">${d.taxStatesTitle}</h2><p>${d.taxStatesIntro}</p></div><span class="state-count">5</span></div>
      <div class="tax-state-list">
        ${taxStates.map((state) => `<article class="tax-state-card ${state.localTaxPossible ? 'has-local-tax' : ''}">
          <div class="state-code" aria-hidden="true">${state.code}</div>
          <div><h3>${state.name[locale]}</h3><span class="tax-status"><i class="ph ${state.localTaxPossible ? 'ph-warning' : 'ph-check-circle'}" aria-hidden="true"></i>${state.localTaxPossible ? d.localTaxPossible : d.noGeneralTax}</span><p>${d.stateNotes[state.code]}</p></div>
          <a href="${state.sourceUrl}" target="_blank" rel="noreferrer" aria-label="${sourceLabel}: ${state.name[locale]}"><i class="ph ph-arrow-up-right" aria-hidden="true"></i><span>${sourceLabel}</span></a>
        </article>`).join('')}
      </div>
    </section>
    <section class="platform-grid" aria-label="Apple and Steam tax notes">
      <article><span class="platform-icon apple-icon"><i class="ph ph-apple-logo" aria-hidden="true"></i></span><h2>${d.appleTitle}</h2><p>${d.appleBody}</p></article>
      <article><span class="platform-icon steam-icon"><i class="ph ph-steam-logo" aria-hidden="true"></i></span><h2>${d.steamTitle}</h2><p>${d.steamBody}</p></article>
    </section>
    <aside class="tax-warning"><i class="ph ph-shield-warning" aria-hidden="true"></i><div><h2>${d.taxWarningTitle}</h2><p>${d.taxWarning}</p></div></aside>
    <section class="source-section" aria-labelledby="official-sources-heading"><h2 id="official-sources-heading">${d.officialSources}</h2><ul>
      ${platformTaxSources.map((source) => `<li><a href="${source.url}" target="_blank" rel="noreferrer">${source.label[locale]}<i class="ph ph-arrow-up-right" aria-hidden="true"></i></a></li>`).join('')}
    </ul></section>
  </main>`;
}

function popularCities() {
  const wanted = filters.taxFreeOnly
    ? ['Portland', 'Wilmington', 'Bozeman', 'Manchester', 'Anchorage']
    : ['New York','Los Angeles','Chicago','Houston','Phoenix','Philadelphia','San Antonio','San Diego','Dallas','San Jose'];
  const available = scopedLocations();
  return wanted.flatMap((city) => {
    const hit = available.find((item) => item.c === city);
    return hit ? [{ c: city, s: hit.s }] : [];
  });
}

function bindNavigation() {
  document.querySelectorAll<HTMLAnchorElement>('[data-nav], [data-language]').forEach((link) => link.addEventListener('click', (event) => {
    event.preventDefault();
    const nextLocale = link.dataset.language as Locale | undefined;
    if (nextLocale) {
      locale = nextLocale;
      localStorage.setItem('locale', locale);
    }
    const nav = link.dataset.nav as Page | undefined;
    if (nav) page = nav;
    history.pushState({}, '', link.href);
    render();
  }));
}

function bindHome() {
  const stateSelect = document.querySelector<HTMLSelectElement>('#stateSelect');
  const cityInput = document.querySelector<HTMLInputElement>('#cityInput');
  const cityCombobox = document.querySelector<HTMLDivElement>('#cityCombobox');
  const cityOptions = document.querySelector<HTMLDivElement>('#cityOptions');
  const taxFreeOnly = document.querySelector<HTMLInputElement>('#taxFreeOnly');
  const houseNumber = document.querySelector<HTMLInputElement>('#houseNumber');
  const d = dictionaries[locale];

  const cityChoices = (() => {
    const seen = new Set<string>();
    return scopedLocations()
      .filter((item) => !filters.state || item.s === filters.state)
      .filter((item) => {
        const key = `${item.s}|${item.c}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.c.localeCompare(b.c));
  })();

  const closeCityOptions = () => {
    if (!cityOptions || !cityInput) return;
    cityOptions.hidden = true;
    cityInput.setAttribute('aria-expanded', 'false');
  };

  const selectCity = (city: string, state: string) => {
    filters.city = city;
    if (!filters.state) filters.state = state;
    saveFilters();
    createIdentity();
    render({ scrollToTop: false, restoreFocus: '#cityInput' });
  };

  const updateCityOptions = () => {
    if (!cityOptions || !cityInput) return;
    const inputValue = cityInput.value.trim();
    const query = inputValue === filters.city ? '' : inputValue.toLocaleLowerCase();
    const choices = cityChoices.filter((item) => !query || item.c.toLocaleLowerCase().includes(query)).slice(0, 40);
    const randomOption = !query ? `<button type="button" role="option" data-city-option="" data-state-option=""><span>${d.randomCity}</span><small><i class="ph ph-shuffle" aria-hidden="true"></i></small></button>` : '';
    cityOptions.innerHTML = choices.length
      ? randomOption + choices.map((item) => `<button type="button" role="option" data-city-option="${escapeHtml(item.c)}" data-state-option="${item.s}"><span>${escapeHtml(item.c)}</span><small>${item.s}</small></button>`).join('')
      : `<p class="city-empty">${d.cityNoResults}</p>`;
    cityOptions.hidden = false;
    cityInput.setAttribute('aria-expanded', 'true');
    cityOptions.querySelectorAll<HTMLButtonElement>('[data-city-option]').forEach((option) => option.addEventListener('click', () => selectCity(option.dataset.cityOption || '', option.dataset.stateOption || '')));
  };

  stateSelect?.addEventListener('change', () => {
    filters.state = stateSelect.value;
    filters.city = '';
    saveFilters();
    render({ scrollToTop: false, restoreFocus: '#stateSelect' });
  });
  cityInput?.addEventListener('focus', updateCityOptions);
  cityInput?.addEventListener('input', updateCityOptions);
  cityInput?.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      cityOptions?.querySelector<HTMLButtonElement>('[data-city-option]')?.focus();
    } else if (event.key === 'Escape') {
      closeCityOptions();
    } else if (event.key === 'Enter') {
      const exact = cityChoices.find((item) => item.c.toLocaleLowerCase() === cityInput.value.trim().toLocaleLowerCase());
      if (exact) {
        event.preventDefault();
        selectCity(exact.c, exact.s);
      }
    }
  });
  cityOptions?.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Escape') return;
    event.preventDefault();
    if (event.key === 'Escape') {
      closeCityOptions();
      cityInput?.focus();
      return;
    }
    const options = [...cityOptions.querySelectorAll<HTMLButtonElement>('[data-city-option]')];
    const index = options.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'ArrowDown' ? Math.min(index + 1, options.length - 1) : index <= 0 ? 0 : index - 1;
    options[next]?.focus();
  });
  cityCombobox?.addEventListener('focusout', () => window.setTimeout(() => {
    if (!cityCombobox.contains(document.activeElement)) closeCityOptions();
  }, 0));
  taxFreeOnly?.addEventListener('change', () => {
    filters.taxFreeOnly = taxFreeOnly.checked;
    filters.state = '';
    filters.city = '';
    saveFilters();
    createIdentity();
    render({ scrollToTop: false, restoreFocus: '#taxFreeOnly' });
  });
  houseNumber?.addEventListener('change', () => {
    filters.requireHouseNumber = houseNumber.checked;
    saveFilters();
  });
  document.querySelector('#generateButton')?.addEventListener('click', () => {
    createIdentity();
    render({ scrollToTop: false, restoreFocus: '#generateButton' });
  });
  document.querySelectorAll<HTMLButtonElement>('[data-city]').forEach((button) => button.addEventListener('click', () => {
    filters.state = button.dataset.state || '';
    filters.city = button.dataset.city || '';
    saveFilters();
    createIdentity();
    render({ scrollToTop: false });
  }));
  document.querySelectorAll<HTMLButtonElement>('.copy-field').forEach((button) => button.addEventListener('click', () => copyText(button.dataset.copy || '')));
  document.querySelector('#copyAllButton')?.addEventListener('click', () => currentIdentity && copyText(flattenIdentity(currentIdentity).map(([key, value]) => `${fieldLabels[locale][key] || key}: ${value}`).join('\n')));
  document.querySelector('#exportButton')?.addEventListener('click', exportJson);
  observeMap();
}

async function copyText(text: string) {
  const d = dictionaries[locale];
  try {
    await navigator.clipboard.writeText(text);
    showToast(d.copied);
  } catch {
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.append(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    showToast(ok ? d.copied : d.copyFailed);
  }
}

function showToast(message: string) {
  const toast = document.querySelector<HTMLDivElement>('#toast');
  if (!toast) return;
  toast.textContent = message;
  toast.classList.add('visible');
  window.setTimeout(() => toast.classList.remove('visible'), 1800);
}

function exportJson() {
  if (!currentIdentity) return;
  const blob = new Blob([JSON.stringify(currentIdentity, null, 2)], { type: 'application/json' });
  const anchor = document.createElement('a');
  anchor.href = URL.createObjectURL(blob);
  anchor.download = `us-test-identity-${currentIdentity.address.state.toLowerCase()}-${currentIdentity.address.zipCode}.json`;
  anchor.click();
  URL.revokeObjectURL(anchor.href);
}

function observeMap() {
  const element = document.querySelector<HTMLDivElement>('#map');
  if (!element) return;
  const observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      observer.disconnect();
      void initializeMap(element);
    }
  }, { rootMargin: '160px' });
  observer.observe(element);
  mapCleanup = () => observer.disconnect();
}

async function initializeMap(element: HTMLDivElement) {
  try {
    const L = await import('leaflet');
    if (!element.isConnected) return;
    element.replaceChildren();
    const lat = Number(element.dataset.lat);
    const lng = Number(element.dataset.lng);
    const map = L.map(element, { scrollWheelZoom: false, zoomControl: true }).setView([lat, lng], 10);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 16, attribution: '&copy; OpenStreetMap contributors' }).addTo(map);
    L.circleMarker([lat, lng], { radius: 8, color: '#ffffff', weight: 3, fillColor: '#2457d6', fillOpacity: 1 }).addTo(map).bindPopup(element.dataset.label || '').openPopup();
    mapCleanup = () => map.remove();
  } catch (error) {
    console.warn('Map unavailable', error);
  }
}

window.addEventListener('popstate', () => {
  locale = getLocale();
  page = getPage();
  render();
});

void loadData();
